// ═══════════════════════════════════════════════════════════════
// HelpPanel — Contextual help slide-in, workflow-first
// Path: src/components/ui/HelpPanel.jsx
//
// Rendered by ZoneOneHeader when helpContent is provided.
// Tabbed header: Workflows (imperative, do-this-to-get-the-job-done)
// and Reference (descriptive, what-is-this-I'm-looking-at).
//
// Falls back gracefully when a HELP entry has no workflows defined —
// the tabs hide and the panel renders the reference content directly.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import {
  X,
  HelpCircle,
  ChevronRight,
  CheckCircle2,
  Clock,
  Target,
  ArrowRight,
  BookOpen,
  Search,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

// ── Helpers ──────────────────────────────────────────────────────

// Legacy alias — keep `sections` working after renaming to `reference`.
const getReference = (helpContent) =>
  (helpContent && (helpContent.reference || helpContent.sections)) || [];

const hasWorkflows = (helpContent) =>
  Array.isArray(helpContent?.workflows) && helpContent.workflows.length > 0;

// Split query on whitespace and require ALL tokens to match somewhere
// in the given searchable string. Case-insensitive.
const matchesQuery = (text, tokens) => {
  if (!tokens.length) return true;
  const t = (text || "").toLowerCase();
  return tokens.every((tok) => t.includes(tok));
};

// A workflow matches if any of its fields (title / goal / done / step
// text) contains every query token. Ensures "log contact" hits a
// workflow whose step mentions "Log a contact from the profile."
const workflowMatchesQuery = (wf, tokens) => {
  if (!tokens.length) return true;
  const haystack = [
    wf.title,
    wf.goal,
    wf.done,
    ...(wf.steps || []).map((s) => s.text),
  ]
    .filter(Boolean)
    .join(" | ");
  return matchesQuery(haystack, tokens);
};

// A reference section matches if the heading or any item contains
// every query token.
const sectionMatchesQuery = (section, tokens) => {
  if (!tokens.length) return true;
  const haystack = [section.heading, ...(section.items || [])]
    .filter(Boolean)
    .join(" | ");
  return matchesQuery(haystack, tokens);
};

const tokenize = (query) =>
  (query || "")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);

// ── Sub-components ───────────────────────────────────────────────

const TabBar = ({ active, onChange, palette, workflowCount, referenceCount }) => {
  const tabStyle = (isActive) => ({
    flex: 1,
    padding: "10px 14px",
    background: isActive ? "#ffffff" : "transparent",
    color: isActive ? palette.accent1 || "#6366f1" : "#64748b",
    border: "none",
    borderBottom: isActive
      ? `2px solid ${palette.accent1 || "#6366f1"}`
      : "2px solid transparent",
    fontSize: FONT_SIZES.sm,
    fontWeight: 700,
    fontFamily: FONT_FAMILY,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    letterSpacing: 0.2,
    transition: "all 0.15s",
  });

  return (
    <div
      style={{
        display: "flex",
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        flexShrink: 0,
      }}
    >
      <button
        onClick={() => onChange("workflows")}
        style={tabStyle(active === "workflows")}
      >
        <Target size={ICON_SIZES.sm} />
        Workflows
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 6px",
            borderRadius: 99,
            background: active === "workflows" ? "#e0e7ff" : "#e2e8f0",
            color: "#64748b",
            minWidth: 18,
            textAlign: "center",
          }}
        >
          {workflowCount}
        </span>
      </button>
      <button
        onClick={() => onChange("reference")}
        style={tabStyle(active === "reference")}
      >
        <BookOpen size={ICON_SIZES.sm} />
        Reference
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            padding: "1px 6px",
            borderRadius: 99,
            background: active === "reference" ? "#e0e7ff" : "#e2e8f0",
            color: "#64748b",
            minWidth: 18,
            textAlign: "center",
          }}
        >
          {referenceCount}
        </span>
      </button>
    </div>
  );
};

const KeyboardBadge = ({ token }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "1px 6px",
      borderRadius: 4,
      background: "#1f2937",
      color: "#f9fafb",
      fontSize: 10,
      fontWeight: 700,
      fontFamily:
        "'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
      letterSpacing: 0.5,
      marginRight: 3,
    }}
  >
    {token}
  </span>
);

const StepRow = ({ step, index, isLast, palette, onCta }) => (
  <div style={{ display: "flex", gap: 12, position: "relative" }}>
    {/* Timeline track (vertical line + numbered circle) */}
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: palette.accent1 || "#6366f1",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </div>
      {!isLast && (
        <div
          style={{
            width: 2,
            flex: 1,
            background: `${palette.accent1 || "#6366f1"}30`,
            marginTop: 2,
          }}
        />
      )}
    </div>

    {/* Step body */}
    <div style={{ flex: 1, paddingBottom: isLast ? 0 : 14, minWidth: 0 }}>
      <div
        style={{
          fontSize: FONT_SIZES.sm,
          color: "#1f2937",
          lineHeight: 1.55,
        }}
      >
        {step.text}
      </div>
      {step.kbd && step.kbd.length > 0 && (
        <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 2 }}>
          {step.kbd.map((token, i) => (
            <React.Fragment key={i}>
              <KeyboardBadge token={token} />
              {i < step.kbd.length - 1 && (
                <span
                  style={{
                    fontSize: 10,
                    color: "#94a3b8",
                    fontFamily: FONT_FAMILY,
                    marginRight: 3,
                  }}
                >
                  then
                </span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      {step.cta && step.cta.kind === "nav" && (
        <button
          onClick={() => onCta(step.cta)}
          style={{
            marginTop: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            borderRadius: 6,
            border: "none",
            background: palette.accent1 || "#6366f1",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            fontFamily: FONT_FAMILY,
            cursor: "pointer",
            letterSpacing: 0.2,
          }}
        >
          {step.cta.label || "Open"}
          <ArrowRight size={ICON_SIZES.xs} />
        </button>
      )}
    </div>
  </div>
);

const WorkflowCard = ({ workflow, index, palette, onCta }) => (
  <div
    style={{
      marginBottom: 18,
      padding: "16px 18px",
      borderRadius: 12,
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}
  >
    {/* Header: number + title + time */}
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          minWidth: 24,
          height: 24,
          padding: "0 7px",
          borderRadius: 12,
          background: `${palette.accent1 || "#6366f1"}15`,
          color: palette.accent1 || "#6366f1",
          fontSize: 11,
          fontWeight: 800,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          letterSpacing: 0.3,
        }}
      >
        #{index + 1}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.3,
          }}
        >
          {workflow.title}
        </div>
        {workflow.timeEstimate && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              marginTop: 4,
              fontSize: 11,
              color: "#64748b",
              fontWeight: 600,
            }}
          >
            <Clock size={ICON_SIZES.xs} />
            {workflow.timeEstimate}
          </div>
        )}
      </div>
    </div>

    {/* Goal callout */}
    {workflow.goal && (
      <div
        style={{
          padding: "8px 11px",
          borderRadius: 7,
          background: `${palette.accent1 || "#6366f1"}08`,
          borderLeft: `3px solid ${palette.accent1 || "#6366f1"}`,
          fontSize: 12,
          color: "#475569",
          lineHeight: 1.5,
          marginBottom: 12,
          fontStyle: "italic",
        }}
      >
        {workflow.goal}
      </div>
    )}

    {/* Steps */}
    <div>
      {(workflow.steps || []).map((step, i) => (
        <StepRow
          key={i}
          step={step}
          index={i}
          isLast={i === workflow.steps.length - 1}
          palette={palette}
          onCta={onCta}
        />
      ))}
    </div>

    {/* Done when */}
    {workflow.done && (
      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px dashed #e2e8f0",
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
          fontSize: 12,
          color: "#059669",
          lineHeight: 1.5,
          fontWeight: 500,
        }}
      >
        <CheckCircle2
          size={ICON_SIZES.sm}
          style={{ flexShrink: 0, marginTop: 1 }}
        />
        <span>
          <strong>Done when: </strong>
          {workflow.done}
        </span>
      </div>
    )}
  </div>
);

const ReferenceSection = ({ section, palette }) => (
  <div style={{ marginTop: 20 }}>
    <div
      style={{
        fontSize: FONT_SIZES.xs,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: palette.accent1 || "#6366f1",
        marginBottom: 10,
      }}
    >
      {section.heading}
    </div>
    {(section.items || []).map((item, ii) => (
      <div
        key={ii}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <ChevronRight
          size={ICON_SIZES.sm}
          style={{
            color: palette.accent1 || "#6366f1",
            marginTop: 3,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: FONT_SIZES.sm,
            color: "#374151",
            lineHeight: 1.55,
          }}
        >
          {item}
        </span>
      </div>
    ))}
  </div>
);

// ── Main HelpPanel ───────────────────────────────────────────────

const HelpPanel = ({ isOpen, onClose, helpContent }) => {
  const { palette, navigate } = useApp();
  const panelRef = useRef(null);

  const workflows = helpContent?.workflows || [];
  const reference = getReference(helpContent);
  const showTabs = hasWorkflows(helpContent) && reference.length > 0;
  const defaultTab = hasWorkflows(helpContent) ? "workflows" : "reference";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [query, setQuery] = useState("");

  // Reset tab + search when help content changes (different view's help)
  useEffect(() => {
    setActiveTab(defaultTab);
    setQuery("");
  }, [helpContent, defaultTab]);

  // Also clear the query when the panel closes so re-opening starts fresh.
  useEffect(() => {
    if (!isOpen) setQuery("");
  }, [isOpen]);

  // Filtered lists — recompute on every keystroke. Small arrays, cheap.
  const tokens = tokenize(query);
  const filteredWorkflows = workflows.filter((w) =>
    workflowMatchesQuery(w, tokens)
  );
  const filteredReference = reference.filter((s) =>
    sectionMatchesQuery(s, tokens)
  );
  const hasQuery = tokens.length > 0;

  // When the user types a query that has 0 hits in the active tab but
  // hits in the other tab, nudge them by auto-switching.
  useEffect(() => {
    if (!hasQuery) return;
    if (activeTab === "workflows" && filteredWorkflows.length === 0 && filteredReference.length > 0) {
      setActiveTab("reference");
    } else if (activeTab === "reference" && filteredReference.length === 0 && filteredWorkflows.length > 0) {
      setActiveTab("workflows");
    }
  }, [query]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (panelRef.current && !panelRef.current.contains(e.target)) {
      onClose();
    }
  };

  const handleCta = (cta) => {
    if (!cta || cta.kind !== "nav" || !cta.view) return;
    onClose();
    if (typeof navigate === "function") {
      navigate(cta.view, null, cta.openHelp ? { openHelp: true } : null);
    }
  };

  if (!helpContent) return null;

  const renderedContent =
    !showTabs && hasWorkflows(helpContent)
      ? "workflows"
      : !showTabs && reference.length > 0
      ? "reference"
      : activeTab;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        style={{
          position: "fixed",
          inset: 0,
          background: isOpen ? "rgba(0,0,0,0.25)" : "transparent",
          zIndex: 1100,
          pointerEvents: isOpen ? "auto" : "none",
          transition: "background 0.25s ease",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: 440,
          maxWidth: "94vw",
          height: "100vh",
          background: "#f8fafc",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
          zIndex: 1101,
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
        }}
      >
        {/* Brand banner */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #0b1220 0%, #1e293b 55%, #334155 100%)",
            padding: "22px 20px 18px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -40,
              right: -40,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.10)",
              filter: "blur(4px)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              position: "relative",
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.28)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HelpCircle size={ICON_SIZES.lg} color="#fff" />
            </div>
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.6)",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  marginBottom: 2,
                }}
              >
                {helpContent.title}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#ffffff",
                  fontFamily: FONT_FAMILY,
                  lineHeight: 1,
                }}
              >
                How do I…?
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            title="Close help (Esc)"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8,
              padding: "6px 8px",
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            <X size={ICON_SIZES.sm} />
          </button>
        </div>

        {/* Intro */}
        {helpContent.intro && (
          <div
            style={{
              padding: "16px 20px",
              fontSize: FONT_SIZES.sm,
              color: "#475569",
              lineHeight: 1.6,
              background: "#ffffff",
              borderBottom: "1px solid #e2e8f0",
              flexShrink: 0,
            }}
          >
            {helpContent.intro}
          </div>
        )}

        {/* Search */}
        <div
          style={{
            padding: "10px 20px",
            background: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            flexShrink: 0,
          }}
        >
          <div style={{ position: "relative" }}>
            <Search
              size={ICON_SIZES.sm}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                pointerEvents: "none",
              }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this help…"
              style={{
                width: "100%",
                padding: "8px 32px 8px 32px",
                borderRadius: 8,
                fontSize: FONT_SIZES.sm,
                fontFamily: FONT_FAMILY,
                outline: "none",
                border: "1px solid #e2e8f0",
                background: "#f8fafc",
                color: "#1f2937",
                boxSizing: "border-box",
              }}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                title="Clear search"
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#e2e8f0",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <X size={ICON_SIZES.xs} style={{ color: "#64748b" }} />
              </button>
            )}
          </div>
          {hasQuery && (
            <div
              style={{
                marginTop: 6,
                fontSize: 11,
                color: "#64748b",
                fontFamily: FONT_FAMILY,
              }}
            >
              {filteredWorkflows.length + filteredReference.length} match
              {filteredWorkflows.length + filteredReference.length === 1
                ? ""
                : "es"}
              {" · "}
              <button
                onClick={() => setQuery("")}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: palette.accent1 || "#6366f1",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: FONT_FAMILY,
                }}
              >
                clear
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        {showTabs && (
          <TabBar
            active={activeTab}
            onChange={setActiveTab}
            palette={palette}
            workflowCount={filteredWorkflows.length}
            referenceCount={filteredReference.length}
          />
        )}

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px 20px",
          }}
        >
          {renderedContent === "workflows" &&
            filteredWorkflows.length > 0 && (
              <>
                {filteredWorkflows.map((wf, i) => (
                  <WorkflowCard
                    key={wf.id || i}
                    workflow={wf}
                    index={i}
                    palette={palette}
                    onCta={handleCta}
                  />
                ))}
              </>
            )}

          {renderedContent === "reference" && filteredReference.length > 0 && (
            <>
              {filteredReference.map((section, si) => (
                <ReferenceSection
                  key={si}
                  section={section}
                  palette={palette}
                />
              ))}
            </>
          )}

          {renderedContent === "workflows" &&
            filteredWorkflows.length === 0 && (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: FONT_SIZES.sm,
                  lineHeight: 1.6,
                }}
              >
                {hasQuery ? (
                  <>
                    No workflows match &ldquo;{query}&rdquo;.
                    {filteredReference.length > 0 && (
                      <>
                        {" "}
                        <button
                          onClick={() => setActiveTab("reference")}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: palette.accent1 || "#6366f1",
                            fontWeight: 600,
                            cursor: "pointer",
                            fontSize: FONT_SIZES.sm,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          Try Reference ({filteredReference.length})
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  "No workflows written for this view yet. Check the Reference tab for descriptive help."
                )}
              </div>
            )}

          {renderedContent === "reference" &&
            filteredReference.length === 0 && (
              <div
                style={{
                  padding: 30,
                  textAlign: "center",
                  color: "#94a3b8",
                  fontSize: FONT_SIZES.sm,
                  lineHeight: 1.6,
                }}
              >
                {hasQuery ? (
                  <>
                    Nothing in Reference matches &ldquo;{query}&rdquo;.
                    {filteredWorkflows.length > 0 && (
                      <>
                        {" "}
                        <button
                          onClick={() => setActiveTab("workflows")}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: palette.accent1 || "#6366f1",
                            fontWeight: 600,
                            cursor: "pointer",
                            fontSize: FONT_SIZES.sm,
                            fontFamily: FONT_FAMILY,
                          }}
                        >
                          Try Workflows ({filteredWorkflows.length})
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  "No reference content available."
                )}
              </div>
            )}
        </div>

        {/* Pro tip footer */}
        {helpContent.tip && (
          <div
            style={{
              margin: "0 20px 16px",
              background: "#fef3c7",
              border: "1px solid #fde68a",
              borderRadius: 10,
              padding: "12px 14px",
              fontSize: FONT_SIZES.xs,
              color: "#92400e",
              lineHeight: 1.55,
              flexShrink: 0,
            }}
          >
            <span style={{ fontWeight: 700 }}>💡 Tip: </span>
            {helpContent.tip}
          </div>
        )}
      </div>
    </>
  );
};

export default HelpPanel;
